"use client";

import { Flex, Text } from "@chakra-ui/react";
import { Checkbox } from "@/components/shared/formElements/Checkbox";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { FiChevronDown } from "react-icons/fi";
import type { GetAvailableClassResponse } from "@/openapi/generated";
import { formatTimeTo12Hour } from "@/lib/utils";

export function useColumns({
  selectedClasses,
  expandedClassId,
  toggleClassSelection,
  toggleExpand,
  textColor,
}: {
  selectedClasses: number[];
  expandedClassId: number | null;
  toggleClassSelection: (id: number) => void;
  toggleExpand: (id: number) => void;
  textColor: string;
}) {
  const columnHelper = createColumnHelper<GetAvailableClassResponse>();

  return useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: () => <Text color="gray.400">SELECT</Text>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Flex height="40px" align="center" justify="center">
              <Checkbox
                isChecked={selectedClasses.includes(Number(row.classId))}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleClassSelection(Number(row.classId));
                }}
              />
            </Flex>
          );
        },
      }),

      columnHelper.accessor("name", {
        id: "name",
        header: () => <Text color="gray.400">CLASS</Text>,
        cell: (info) => (
          <Text fontSize="sm" fontWeight="700" color={textColor}>
            {info.row.original.name}
          </Text>
        ),
      }),

      columnHelper.accessor("classDays", {
        id: "classDays",
        header: () => <Text color="gray.400">SCHEDULE</Text>,
        cell: (info) => {
          const days = info.row.original.classDays || [];
          if (!days.length) return <Text color="secondaryGray.500">-</Text>;

          return (
            <Flex direction="column" rowGap="4px">
              {days.map((d, i) => (
                <Flex key={i} justify="space-between">
                  <Text fontWeight="600" color={textColor}>
                    {d.day}:
                  </Text>
                  <Text color="secondaryGray.500">
                    {formatTimeTo12Hour(d.startTime)} –{" "}
                    {formatTimeTo12Hour(d.endTime)}
                  </Text>
                </Flex>
              ))}
            </Flex>
          );
        },
      }),

      columnHelper.accessor("location", {
        id: "facility",
        header: () => <Text color="gray.400">FACILITY</Text>,
        cell: (info) => {
          const row = info.row.original;
          const items = [
            row.classRoom?.name && {
              label: "Classroom",
              value: row.classRoom.name,
            },
            row.location?.name && {
              label: "Location",
              value: row.location.name,
            },
          ].filter(Boolean) as { label: string; value: string }[];

          if (!items.length) return <Text color="secondaryGray.500">-</Text>;

          return (
            <Flex direction="column" rowGap="4px">
              {items.map((item, index) => (
                <Flex key={index} justify="space-between">
                  <Text fontWeight="600" color={textColor}>
                    {item.label}:
                  </Text>
                  <Text color="secondaryGray.500">{item.value}</Text>
                </Flex>
              ))}
            </Flex>
          );
        },
      }),

      columnHelper.display({
        id: "expand",
        header: () => null,
        cell: (info) => {
          const row = info.row.original;
          const isExpanded = expandedClassId === Number(row.classId);

          return (
            <Flex
              as="button"
              align="center"
              justify="center"
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(Number(row.classId));
              }}
            >
              <FiChevronDown
                style={{
                  width: 20,
                  height: 20,
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </Flex>
          );
        },
      }),
    ],
    [selectedClasses, expandedClassId, textColor],
  );
}
